/**
 * Video start time deep links (videos metaobject pages).
 *
 * Lets a support agent append a start time to the page URL so the customer's
 * playback begins at the moment the agent intended:
 *
 *   /pages/videos/getting-started?t=90       -> 1:30
 *   /pages/videos/getting-started?t=1:30     -> 1:30
 *   /pages/videos/getting-started?t=1m30s    -> 1:30
 *
 * The video is NOT autoplayed and the page is otherwise untouched — the seek is
 * applied to the first play only, so scrubbing and replays behave normally.
 * With no `t` param this script does nothing at all.
 */
(() => {
  const SEEK_APPLIED = Symbol('videoStartTimeApplied');

  /**
   * Parses a start time into seconds. Accepts plain seconds ("90"), clock
   * notation ("1:30", "1:02:03") and unit notation ("1m30s", "90s").
   * @param {string} value
   * @returns {number|null} Seconds, or null when unparseable.
   */
  const parseStartTime = (value) => {
    const raw = (value || '').trim().toLowerCase();
    if (raw === '') return null;

    if (/^\d+(\.\d+)?$/.test(raw)) return parseFloat(raw);

    if (raw.includes(':')) {
      const parts = raw.split(':');
      if (parts.length > 3 || parts.some((part) => !/^\d+(\.\d+)?$/.test(part))) return null;

      return parts.reduce((total, part) => total * 60 + parseFloat(part), 0);
    }

    const units = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+(?:\.\d+)?)s)?$/);
    if (!units || units[0] === '') return null;

    const [, hours, minutes, seconds] = units;
    return Number(hours || 0) * 3600 + Number(minutes || 0) * 60 + Number(seconds || 0);
  };

  /**
   * Seeks a video to `startTime` once, before its first frame of playback.
   * @param {HTMLVideoElement} video
   * @param {number} startTime
   */
  const applyStartTime = (video, startTime) => {
    if (video[SEEK_APPLIED]) return;

    const seek = () => {
      if (video[SEEK_APPLIED]) return;
      video[SEEK_APPLIED] = true;

      // Metadata gives us the duration; clamp so an out-of-range timestamp
      // falls back to playing from the start rather than dropping the customer
      // at the very end of the video.
      if (Number.isFinite(video.duration) && startTime >= video.duration) return;

      try {
        video.currentTime = startTime;
      } catch (error) {
        // Seeking can throw if the media errors out before it is ready. The
        // video still plays from the start, so there is nothing to recover.
      }
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seek();
    } else {
      video.addEventListener('loadedmetadata', seek, { once: true });
      // Safety net: if metadata never fires but playback somehow starts, seek
      // rather than leaving the customer at 0:00.
      video.addEventListener('play', seek, { once: true });
    }
  };

  /**
   * Watches a deferred-media element for the <video> it injects when the
   * customer presses play, then applies the start time to it.
   * @param {Element} container
   * @param {number} startTime
   */
  const watchForVideo = (container, startTime) => {
    const existing = container.querySelector('video');
    if (existing) {
      applyStartTime(existing, startTime);
      return;
    }

    const observer = new MutationObserver(() => {
      const video = container.querySelector('video');
      if (!video) return;

      observer.disconnect();
      applyStartTime(video, startTime);
    });

    observer.observe(container, { childList: true, subtree: true });
  };

  const init = () => {
    const startTime = parseStartTime(new URLSearchParams(window.location.search).get('t'));
    if (startTime === null || startTime <= 0) return;

    document
      .querySelectorAll('deferred-media')
      .forEach((container) => watchForVideo(container, startTime));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
